import React from 'react';
import { motion } from 'framer-motion';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'white';
  fullScreen?: boolean;
  message?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  fullScreen = false,
  message,
}) => {
  const sizeConfig = {
    sm: { container: 40, cube: 20, particle: 3, text: 'text-xs' },
    md: { container: 60, cube: 30, particle: 5, text: 'text-sm' },
    lg: { container: 80, cube: 40, particle: 6, text: 'text-base' },
    xl: { container: 120, cube: 60, particle: 8, text: 'text-lg' },
  };

  const colorConfig = {
    primary: {
      light: '#22c55e',
      medium: '#16a34a',
      dark: '#15803d',
      glow: 'rgba(34, 197, 94, 0.4)',
    },
    secondary: {
      light: '#14b8a6',
      medium: '#0d9488',
      dark: '#0f766e',
      glow: 'rgba(20, 184, 166, 0.4)',
    },
    white: {
      light: '#ffffff',
      medium: '#f1f5f9',
      dark: '#e2e8f0',
      glow: 'rgba(255, 255, 255, 0.4)',
    },
  };

  const { container, cube, particle, text: textSize } = sizeConfig[size];
  const colors = colorConfig[color];

  const spinner = (
    <div className="flex flex-col items-center justify-center gap-6">
      <div 
        className="relative"
        style={{ 
          width: container, 
          height: container,
          perspective: '1000px',
        }}
        role="status"
        aria-label="Loading"
      >
        {/* 3D Rotating Cube */}
        <motion.div
          className="relative w-full h-full"
          style={{
            transformStyle: 'preserve-3d',
          }}
          animate={{
            rotateX: [0, 360],
            rotateY: [0, 360],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          {/* Front Face */}
          <motion.div
            className="absolute inset-0 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.light}, ${colors.medium})`,
              transform: `translateZ(${cube / 2}px)`,
              boxShadow: `0 0 20px ${colors.glow}`,
            }}
            animate={{
              boxShadow: [
                `0 0 20px ${colors.glow}`,
                `0 0 40px ${colors.glow}`,
                `0 0 20px ${colors.glow}`,
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          
          {/* Back Face */}
          <motion.div
            className="absolute inset-0 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.medium}, ${colors.dark})`,
              transform: `translateZ(-${cube / 2}px) rotateY(180deg)`,
              boxShadow: `0 0 20px ${colors.glow}`,
            }}
          />
          
          {/* Right Face */}
          <motion.div
            className="absolute inset-0 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.light}, ${colors.medium})`,
              transform: `rotateY(90deg) translateZ(${cube / 2}px)`,
              boxShadow: `0 0 20px ${colors.glow}`,
            }}
          />
          
          {/* Left Face */}
          <motion.div
            className="absolute inset-0 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.medium}, ${colors.dark})`,
              transform: `rotateY(-90deg) translateZ(${cube / 2}px)`,
              boxShadow: `0 0 20px ${colors.glow}`,
            }}
          />
          
          {/* Top Face */}
          <motion.div
            className="absolute inset-0 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.light}, ${colors.medium})`,
              transform: `rotateX(90deg) translateZ(${cube / 2}px)`,
              boxShadow: `0 0 20px ${colors.glow}`,
            }}
          />
          
          {/* Bottom Face */}
          <motion.div
            className="absolute inset-0 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.medium}, ${colors.dark})`,
              transform: `rotateX(-90deg) translateZ(${cube / 2}px)`,
              boxShadow: `0 0 20px ${colors.glow}`,
            }}
          />
        </motion.div>

        {/* Orbiting Particles */}
        {[0, 120, 240].map((angle, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: particle,
              height: particle,
              background: colors.light,
              boxShadow: `0 0 15px ${colors.glow}`,
              top: '50%',
              left: '50%',
              marginTop: -particle / 2,
              marginLeft: -particle / 2,
            }}
            animate={{
              rotate: [angle, angle + 360],
              x: [
                Math.cos((angle * Math.PI) / 180) * (container / 2),
                Math.cos(((angle + 360) * Math.PI) / 180) * (container / 2),
              ],
              y: [
                Math.sin((angle * Math.PI) / 180) * (container / 2),
                Math.sin(((angle + 360) * Math.PI) / 180) * (container / 2),
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear',
              delay: i * 0.2,
            }}
          />
        ))}

        {/* Pulsing Ring */}
        <motion.div
          className="absolute inset-0 rounded-full border-2"
          style={{
            borderColor: colors.light,
            opacity: 0.3,
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.3, 0, 0.3],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      {message && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${textSize} font-medium text-gray-700 dark:text-gray-300`}
        >
          {message}
        </motion.p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md z-50 flex items-center justify-center">
        {spinner}
      </div>
    );
  }

  return spinner;
};

export default LoadingSpinner;
